import { memberFetch } from "@/lib/api/memberFetch";
import { isUiLocale, normalizeUiLocale, type MemberUiLocale } from "@sukha/shared/locale/messageLocale";
import { createContextLogger } from "@/lib/utils/logger";
import { resolveApiBaseUrl } from "@/lib/api/baseUrl";

const speakLogger = createContextLogger("AssistantSpeakClient");

export const getAssistantMessage = async (
  type?: string,
  subType?: string,
  immediate?: boolean,
  token?: string
) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  // Dynamically build query parameters
  const queryParams = new URLSearchParams();
  if (type) queryParams.append("type", type);
  if (subType) queryParams.append("subType", subType);
  if (immediate !== undefined)
    queryParams.append("immediate", immediate.toString());

  const queryString = queryParams.toString()
    ? `?${queryParams.toString()}`
    : "";

  const response = await memberFetch(`${baseUrl}/api/v1/assistant${queryString}`, {
    method: "GET",
    headers: requestHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to fetch assistant message");
  }
  return response.json();
};

export interface AssistantAudioResult {
  buffer: ArrayBuffer;
  contentType: string;
}

/**
 * The line an onboarding step speaks, in the voice of the assistant this
 * member sees. Names the step and nothing else — the server owns the text
 * and the voice, so this can never be asked to say something arbitrary.
 *
 * @param flowKey - The flow (`setup`, `session1`)
 * @param stepSlug - The step
 */
export const getOnboardingStepAudio = async (
  flowKey: string,
  stepSlug: string
): Promise<AssistantAudioResult> => {
  const response = await memberFetch(`/api/v1/assistant/speak/onboarding-step`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ flowKey, stepSlug }),
  });
  if (!response.ok) {
    speakLogger.warn(
      { status: response.status, flowKey, stepSlug },
      "Onboarding narration request failed"
    );
    throw new Error("Failed to fetch onboarding narration");
  }
  return {
    buffer: await response.arrayBuffer(),
    contentType: response.headers.get("content-type") ?? "audio/mpeg",
  };
};

export const getAssistantAudio = async (
  text: string,
  token?: string,
  language?: string
): Promise<AssistantAudioResult> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/speak");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/speak`, {
    method: "POST",
    headers: requestHeaders,
    body: JSON.stringify({ text, language }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Failed to parse error response" }));
    speakLogger.error(
      {
        status: response.status,
        statusText: response.statusText,
        error: errorData.error,
        details: errorData.details,
        message: errorData.message,
      },
      "Speak API failed"
    );
    throw new Error(
      errorData.details ||
        errorData.message ||
        errorData.error ||
        "Failed to fetch assistant audio"
    );
  }
  return {
    buffer: await response.arrayBuffer(),
    contentType: response.headers.get("content-type") ?? "audio/wav",
  };
};

export const getActivePersonality = async (token?: string) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/active_personality");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(
    `${baseUrl}/api/v1/assistant/active_personality`,
    {
      method: "GET",
      headers: requestHeaders,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get active personality");
  }
  return response.json();
};

export const getPersonalities = async (token?: string) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/personalities");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/personalities`, {
    method: "GET",
    headers: requestHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get personalities");
  }
  return response.json();
};

export const checkAssistantUsage = async (token?: string) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/usage");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/usage`, {
    method: "GET",
    headers: requestHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to check assistant usage");
  }
  return response.json();
};

export const getMastraMemoryMessages = async (
  thread: string,
  page: number = 0,
  limit: number = 50,
  token?: string
) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/memory");

  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  queryParams.append("perPage", limit.toString());

  const response = await memberFetch(
    `${baseUrl}/api/v1/assistant/memory/${encodeURIComponent(thread)}?${queryParams.toString()}`,
    {
      method: "GET",
      headers: requestHeaders,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get memory messages");
  }
  return response.json();
};

/** Persist chat messages to Mastra memory (e.g. after stream finishes, including client-tool turns).
 * Each message may have parts[]. For help-with-task historical result, the tool part (Show help with task guide)
 * must include output: { status: "cancelled" } or { status: "success", taskId, taskName } so the UI can show
 * "No help was given" or "I got help with task {name}". Preserve full parts (including part.output) when saving/loading. */
export const saveMastraMemoryMessages = async (
  threadId: string,
  messages: Array<{ id?: string; role: string; parts?: unknown; content?: unknown; createdAt?: string | Date }>,
  token?: string
): Promise<{ saved: number }> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/memory/save");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await memberFetch(`${baseUrl}/api/v1/assistant/memory/save`, {
    method: "POST",
    headers,
    body: JSON.stringify({ threadId, messages }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { message?: string }).message || "Failed to save messages to memory");
  }
  return response.json();
};

export interface FlipBriefingResponse {
  skipped: boolean;
  reason?: string;
  phase?: "day_start" | "intraday";
  messageId?: string | null;
  message?: string;
  timestamp?: string;
}

/**
 * Triggers a flip-open assistant briefing on the server. The server applies the throttle
 * (first flip per calendar day vs. >=1 hour since last flip) and only generates when eligible.
 * On generation, the assistant message is saved to the main `assistant-{userId}` Mastra thread,
 * so the client should refresh memory after a non-skipped response.
 */
export const triggerFlipBriefing = async (
  token?: string
): Promise<FlipBriefingResponse> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/flip-briefing");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await memberFetch(`${baseUrl}/api/v1/assistant/flip-briefing`, {
    method: "POST",
    headers,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ||
        "Failed to trigger flip briefing"
    );
  }
  return response.json();
};

export const getDailyQuote = async (token?: string) => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/daily_quote");

  // Initialize headers
  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/daily_quote`, {
    method: "GET",
    headers: requestHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || "Failed to get daily quote"
    );
  }
  return response.json();
};

export const getFocusMessages = async (
  type: string,
  token?: string,
  locale?: MemberUiLocale
): Promise<MemberFocusMessagesResponse> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/focus-messages");
  const localeQuery =
    locale && isUiLocale(locale)
      ? `?locale=${encodeURIComponent(locale)}`
      : "";

  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(
    `${baseUrl}/api/v1/assistant/focus-messages/${type}${localeQuery}`,
    {
      method: "GET",
      headers: requestHeaders,
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || "Failed to get focus messages"
    );
  }
  const data = await response.json();
  return {
    messages: data.messages ?? [],
    requestedLocale: normalizeUiLocale(data.requestedLocale),
    resolvedLocale: normalizeUiLocale(data.resolvedLocale),
  };
};

/**
 * Every focus-message type in one request.
 *
 * The dashboard reads thirteen of them on first load. One request per type made
 * sixteen calls compete for function instances — endpoints answering in ~90ms
 * alone took up to eleven seconds in that burst. The API reads the member's
 * personality and locale once and answers them all together.
 */
export const getAllFocusMessages = async (
  token?: string,
  locale?: MemberUiLocale
): Promise<Record<string, MemberFocusMessagesResponse>> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/focus-messages");
  const localeQuery =
    locale && isUiLocale(locale) ? `?locale=${encodeURIComponent(locale)}` : "";

  const response = await memberFetch(
    `${baseUrl}/api/v1/assistant/focus-messages${localeQuery}`,
    {
      method: "GET",
      headers: token
        ? {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }
        : { "Content-Type": "application/json" },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to get focus messages");
  }

  const data = await response.json();
  const byType = (data.byType ?? {}) as Record<
    string,
    { messages?: string[]; requestedLocale?: string; resolvedLocale?: string }
  >;

  return Object.fromEntries(
    Object.entries(byType).map(([type, entry]) => [
      type,
      {
        messages: entry.messages ?? [],
        requestedLocale: normalizeUiLocale(entry.requestedLocale),
        resolvedLocale: normalizeUiLocale(entry.resolvedLocale),
      },
    ])
  );
};

export interface MemberFocusMessagesResponse {
  messages: string[];
  requestedLocale: MemberUiLocale;
  resolvedLocale: MemberUiLocale;
}

export interface NudgeAudioResponse {
  text: string;
  audio: string; // base64 encoded audio
  contentType?: string;
}

export const getAssistantNudgeAudio = async (
  type: string,
  variables?: Record<string, string>,
  token?: string,
  language?: string
): Promise<NudgeAudioResponse> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/speak/nudge");

  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/speak/nudge`, {
    method: "POST",
    headers: requestHeaders,
    body: JSON.stringify({ type, variables, language }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Failed to parse error response" }));
    speakLogger.error(
      {
        status: response.status,
        statusText: response.statusText,
        error: errorData.error,
        details: errorData.details,
        message: errorData.message,
      },
      "Speak/nudge API failed"
    );
    throw new Error(
      errorData.details ||
        errorData.message ||
        errorData.error ||
        "Failed to fetch assistant nudge audio"
    );
  }
  return response.json();
};

export type TranscribeSessionResponse = {
  clientSecret: string;
  expiresAt: number;
};

export const createTranscribeSession = async (
  language?: string,
  token?: string
): Promise<TranscribeSessionResponse> => {
  const baseUrl = resolveApiBaseUrl("/api/v1/assistant/transcribe/session");

  let requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    requestHeaders = {
      ...requestHeaders,
      Authorization: `Bearer ${token}`,
    };
  }

  const response = await memberFetch(`${baseUrl}/api/v1/assistant/transcribe/session`, {
    method: "POST",
    headers: requestHeaders,
    body: JSON.stringify(language ? { language } : {}),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      error?: string;
      details?: string;
      message?: string;
    };
    throw new Error(
      errorData.details ||
        errorData.message ||
        errorData.error ||
        "Failed to create transcription session"
    );
  }

  return response.json() as Promise<TranscribeSessionResponse>;
};

/** Display identity of the assistant shown on signed-out surfaces. */
export type DefaultAssistantIdentity = {
  name: string | null;
  icon: string | null;
};

/**
 * Default assistant's name and avatar, from the public settings route.
 * Signed-out pages (landing, auth flow) have no member to resolve one for.
 */
export const getDefaultAssistantIdentity =
  async (): Promise<DefaultAssistantIdentity> => {
    const baseUrl = resolveApiBaseUrl("/api/v1/settings/default-assistant");

    const response = await fetch(`${baseUrl}/api/v1/settings/default-assistant`);

    if (!response.ok) {
      throw new Error("Failed to get default assistant");
    }
    return response.json();
  };
